import React, { memo, useCallback, useMemo, useState } from 'react';
import { PasswordContainer } from '../../../../components/PasswordContainer';
import { SettingsPageContainer } from '../../../../components/Settings/SettingsPageContainer';
import { useHistory, useParams } from 'react-router-dom';
import { useMessage } from '../../../../hooks/message';
import { useRPC } from '../../../../hooks/rpc';
import { useKeyboard } from '../../../../hooks/keyboard';
import { useSetSelector } from '../../../../hooks/set-selector';
import { SettingInputComponent } from '../../../../components/Settings/SettingInputComponent';
import { ControlTypeModel } from '../../../../types/control';
import { SettingReadOnlyRowComponent } from '../../../../components/Settings/SettingReadOnlyRowComponent';
import { SettingCheckBoxComponent } from '../../../../components/Settings/SettingCheckboxComponent';
import { SelectItem } from '../../../../components/Select';
import { SettingSelectComponent } from '../../../../components/Settings/SettingSelectComponent';
import { useMapSelector } from '../../../../hooks/map-selector';
import { useTranslation } from "react-i18next";

const BACK_URL = '/developer/control-type';

export const ControlTypeEditScreen = memo(function ControlTypeEditScreen() {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  // const question = useQuestion();
  const { success, failure } = useMessage();
  const [rpc] = useRPC();
  const history = useHistory();
  const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();

  const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
  const bridgeDeviceTypeOptionList: SelectItem<string>[] = useMapSelector(
    state => state.control.bridgeDeviceTypeList,
    useCallback(
      bridgeDeviceType => ({
        label: bridgeDeviceType.name + (bridgeDeviceType.is_io_device ? ' [I]' : ' [G]'),
        value: bridgeDeviceType.id,
      }), []
    ),
    [],
  );
  //sendpacket-255-18-14-44-0-39-0-16-87-65-89-48-56-56-32-76-13-15
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selected = useMemo(() => controlTypeSet[params?.id] || null, []);
  const GLASS_DEVICE_INTERNAL_ICON_ID_OPTION_LIST = useMemo<SelectItem<number>[]>(() => [
    {
      label: t('no_icon'),
      value: -1,
    },
    {
      label: t('blinds'),
      value: 0,
    },
    {
      label: t('combi_boiler'),
      value: 1,
    },
    {
      label: t('dimmer'),
      value: 2,
    },
    {
      label: t('air_cond_energy'),
      value: 3,
    },
    {
      label: t('light'),
      value: 7,
    },
    {
      label: t('plug'),
      value: 8,
    },
    {
      label: t('irrigation'),
      value: 9,
    },
    {
      label: t('contactor'),
      value: 12,
    },
    {
      label: t('water_valve'),
      value: 13,
    },
    {
      label: t('gas_valve'),
      value: 32,
    },
  ], [t]);

  const [name, setName] = useState(selected?.name || '');
  const [showOnScenario, setShowOnScenario] = useState(selected?.show_on_scenario || false);
  const [showOnHomepage, setShowOnHomepage] = useState(selected?.show_on_homepage || false);
  const [askValueInInitialize, setAskValueInInitialize] = useState(selected?.ask_value_in_initialize || false);
  const [glassDeviceInternalIconId, setGlassDeviceInternalIconId] = useState<SelectItem<number>>(
    (selected == null ? GLASS_DEVICE_INTERNAL_ICON_ID_OPTION_LIST[0] :
      GLASS_DEVICE_INTERNAL_ICON_ID_OPTION_LIST.find(option => option.value === selected.glass_device_internal_icon_id)
      || null),
  );
  const [allowedBridgeDeviceTypeIdList, setAllowedBridgeDeviceTypeIdList] = useState<SelectItem<string>[]>(
    (selected == null ? [] : bridgeDeviceTypeOptionList.filter(option => selected.allowed_bridge_device_type_id_list.indexOf(option.value) !== -1)),
  );

  const setShowOnScenarioCheckbox = useCallback(() => {
    setShowOnScenario(showOnScenario => !showOnScenario);
  }, []);

  const setShowOnHomepageCheckbox = useCallback(() => {
    setShowOnHomepage(showOnHomepage => !showOnHomepage);
  }, []);

  const setAskValueInInitializeCheckbox = useCallback(() => {
    setAskValueInInitialize(askValueInInitialize => !askValueInInitialize);
  }, []);

  const onBackPressed = useCallback(() => {
    history.push(BACK_URL);
  }, [history]);

  // const onDeletePressed = useCallback(() => {
  //     question('Kontrol tipi silmek istediğinizden emin misiniz?', () => {
  //         rpc.deleteControlType(selected.id)
  //             .then(() => {
  //                 success('Kontrol tipi başarılı bir şekilde silindi.');
  //                 onBackPressed();
  //             })
  //             .catch(failure);
  //     });
  // }, [question, failure, rpc, success, onBackPressed, selected?.id]);

  const isFieldsValid = useCallback((): boolean => {
    if (name == null || name.length === 0) {
      failure(t('pls_enter_name'));

      return false;
    }

    if (glassDeviceInternalIconId.value == null) {
      failure(t('pls_select_glass_device_icon'));

      return false;
    }

    return true;
  }, [
    t,
    failure,
    name,
    glassDeviceInternalIconId,
  ]);

  const onCreatePressed = useCallback(() => {
    if (!isFieldsValid()) {
      return;
    }

    const controlTypeModel: ControlTypeModel<unknown> = {
      ...selected,
      id: selected?.id || null,
      name,
      show_on_scenario: showOnScenario,
      show_on_homepage: showOnHomepage,
      ask_value_in_initialize: askValueInInitialize,
      glass_device_internal_icon_id: glassDeviceInternalIconId?.value,
      allowed_bridge_device_type_id_list: allowedBridgeDeviceTypeIdList.map(option => option.value),
    };

    return rpc.updateControlType(controlTypeModel)
      .then(() => {
        success(t(selected == null ? 'control_type_successfully_saved' : 'control_type_successfully_edited'));
        onBackPressed();
      }).catch(failure);
  }, [
    t,
    isFieldsValid,
    rpc,
    success,
    failure,
    selected,
    onBackPressed,
    name,
    showOnScenario,
    showOnHomepage,
    askValueInInitialize,
    glassDeviceInternalIconId,
    allowedBridgeDeviceTypeIdList,
  ]);

  const buttons = useMemo(() => [
    // {
    //     text: 'SİL',
    //     className: 'mulberry-wood',
    //     onClick: onDeletePressed,
    //     hidden: !(selected?.id != null),
    // },
    {
      text: t('save'),
      className: 'green',
      onClick: onCreatePressed,
    },
    {
      text: t('cancel'),
      className: 'red',
      url: BACK_URL,
    },
  ], [t, onCreatePressed /*onDeletePressed, selected*/]);

  const onBlur = useCallback(() => {
    setActiveInput(null);
  }, [setActiveInput]);

  return (
    <PasswordContainer requiredRole='developer' isFullscreen={true}>
      <SettingsPageContainer
        backUrl={BACK_URL}
        buttons={buttons}
        className='service-edit'
        currentInput={currentInput}>
        <div className='content-divider'>
          <div className='content-wrapper'>
            <SettingInputComponent
              placeholder={t('write_control_type_name')}
              label={t('control_type_name')}
              value={name}
              setValue={setName}
              setActiveInput={setActiveInput}
              onBlur={onBlur} />
            <SettingReadOnlyRowComponent
              title={t('render_type')}
              value={selected?.render_type || '-'} />
            <SettingCheckBoxComponent
              label={t('show_on_scenario')}
              set={setShowOnScenarioCheckbox}
              value={showOnScenario} />
            <SettingCheckBoxComponent
              label={t('show_on_homepage')}
              set={setShowOnHomepageCheckbox}
              value={showOnHomepage} />
            <SettingCheckBoxComponent
              label={t('ask_on_initialize')}
              set={setAskValueInInitializeCheckbox}
              value={askValueInInitialize} />
          </div>
          <div className='content-wrapper'>
            <SettingSelectComponent
              label={t('glass_device_icon')}
              options={GLASS_DEVICE_INTERNAL_ICON_ID_OPTION_LIST}
              onChange={setGlassDeviceInternalIconId}
              value={glassDeviceInternalIconId} />
            <SettingSelectComponent
              label={t('device_types_number', { num: allowedBridgeDeviceTypeIdList.length.toString() })}
              options={bridgeDeviceTypeOptionList}
              isMulti={true}
              // @ts-ignore
              onChange={setAllowedBridgeDeviceTypeIdList}
              value={allowedBridgeDeviceTypeIdList} />
          </div>
        </div>
      </SettingsPageContainer>
      {
        renderKeyboard()
      }
    </PasswordContainer>
  );
});
